import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of, map } from 'rxjs';
import { environment } from '../../environments/environment.development';

export interface CursoProgreso {
  id_curso: number;
  titulo: string;
  descripcion: string;
  es_premium: boolean;
  fecha_inscripcion: string;
  total_modulos: number;
  total_clases: number;
  clases_completadas: number;
  porcentaje: number;
  progreso_guardado: string;
}

export interface ProgresoResponse {
  ok: boolean;
  data: CursoProgreso[];
  msg?: string;
}

export interface Maestro {
  id_usuario: number;
  nombre: string;
  apellido: string;
  email: string;
}

export interface Clase {
  id_clase: number;
  titulo_clase: string;
  descripcion: string | null;
  orden: number;
  duracion_minutos: number | null;
  url_video: string;
  url_audio: string | null;
  tipo_contenido: 'url_youtube' | 'blog' | 'video_local' | 'podcast';
  contenido_blog: string | null;
  foto_portada: string | null;
  tiempo_lectura_minutos: number | null;
  completada: boolean;
  fecha_completado: string | null;
  recursos: RecursoClase[];
}

export interface RecursoClase {
  id_recurso: number;
  nombre_recurso: string;
  tipo_recurso: string;
  url_recurso: string;
  tamaño_bytes: number;
  descripcion: string | null;
  orden: number;
  fecha_subida: string;
  id_usuario?: number; // Opcional: si existe, es un recurso subido por estudiante
}

export interface Modulo {
  id_modulo: number;
  titulo_modulo: string;
  orden: number;
  descripcion: string | null;
  foto_url: string | null;
  clases: Clase[];
}

export interface Plan {
  id_plan: number;
  nombre_plan: string;
  precio: string;
  duracion_dias: number;
}

export interface ProgresoUsuario {
  clases_completadas: number;
  porcentaje: number;
}

export interface CursoCompleto {
  id_curso: number;
  titulo: string;
  descripcion: string;
  foto_portada: string;
  id_maestro: number;
  fecha_creacion: string;
  es_premium: boolean;
  duracion_minutos: number;
  estado: string;
  total_clases: number;
  maestro: Maestro;
  modulos: Modulo[];
  planes: Plan[];
  progreso_usuario?: ProgresoUsuario;
}

export interface CursosResponse {
  ok: boolean;
  data?: CursoCompleto[];
  msg?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CoursesService {
  private readonly API_URL = environment.apiUrl;

  // Signals para el estado
  cursosProgreso = signal<CursoProgreso[]>([]);
  todosLosCursos = signal<CursoCompleto[]>([]);
  isLoading = signal(false);

  constructor(private http: HttpClient) {}

  /**
   * Obtiene las estadísticas de progreso del estudiante
   */
  getEstadisticasProgreso(): Observable<ProgresoResponse> {
    this.isLoading.set(true);
    
    return this.http.get<ProgresoResponse>(`${this.API_URL}/progreso/mis-cursos`).pipe(
      tap(response => {
        this.isLoading.set(false);
        if (response.ok && response.data) {
          this.cursosProgreso.set(response.data);
        }
      }),
      catchError(error => {
        this.isLoading.set(false);
        console.error('Error al obtener estadísticas de progreso:', error);
        return of({
          ok: false,
          data: [],
          msg: error.error?.msg || 'Error al cargar el progreso'
        });
      })
    );
  }

  /**
   * Calcula el porcentaje de progreso total
   */
  calcularProgresoTotal(): number {
    const cursos = this.cursosProgreso();
    if (cursos.length === 0) return 0;
    
    const totalPorcentaje = cursos.reduce((sum, curso) => sum + curso.porcentaje, 0);
    return Math.round(totalPorcentaje / cursos.length);
  }

  /**
   * Obtiene el total de clases completadas
   */
  getTotalClasesCompletadas(): number {
    const cursos = this.cursosProgreso();
    return cursos.reduce((sum, curso) => sum + curso.clases_completadas, 0);
  }

  /**
   * Obtiene el total de clases
   */
  getTotalClases(): number {
    const cursos = this.cursosProgreso();
    return cursos.reduce((sum, curso) => sum + curso.total_clases, 0);
  }

  /**
   * Filtra cursos por estado
   */
  getCursosPorEstado(estado: 'all' | 'inProgress' | 'completed' | 'notStarted'): CursoProgreso[] {
    const cursos = this.cursosProgreso();
    
    switch(estado) {
      case 'inProgress':
        return cursos.filter(c => c.porcentaje > 0 && c.porcentaje < 100);
      case 'completed':
        return cursos.filter(c => c.porcentaje >= 100);
      case 'notStarted':
        return cursos.filter(c => c.porcentaje === 0);
      default:
        return cursos;
    }
  }

  /**
   * Obtiene todos los cursos disponibles
   */
  getAllCursos(): Observable<CursosResponse> {
    this.isLoading.set(true);
    
    return this.http.get<CursosResponse>(`${this.API_URL}/cursos`).pipe(
      tap(response => {
        this.isLoading.set(false);
        if (response.ok && response.data) {
          this.todosLosCursos.set(response.data);
          console.log('✅ Cursos cargados:', response.data.length);
        }
      }),
      catchError(error => {
        this.isLoading.set(false);
        console.error('❌ Error al obtener cursos:', error);
        return of({
          ok: false,
          data: [],
          msg: error.error?.msg || 'Error al cargar los cursos'
        });
      })
    );
  }

  /**
   * Obtiene un curso específico por ID con todos sus detalles
   */
  getCursoById(id: number): Observable<{ ok: boolean; data?: CursoCompleto; msg?: string }> {
    this.isLoading.set(true);
    
    return this.http.get<{ ok: boolean; data?: CursoCompleto[]; msg?: string }>(`${this.API_URL}/cursos`).pipe(
      tap(() => this.isLoading.set(false)),
      map(response => {
        if (response.ok && response.data) {
          const curso = response.data.find(c => c.id_curso === id);
          if (curso) {
            console.log('✅ Curso cargado:', curso.titulo);
            return {
              ok: true,
              data: curso
            };
          } else {
            return {
              ok: false,
              msg: 'Curso no encontrado'
            };
          }
        }
        return {
          ok: false,
          msg: response.msg || 'Error al cargar el curso'
        };
      }),
      catchError(error => {
        this.isLoading.set(false);
        console.error('❌ Error al obtener curso por ID:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar el curso'
        });
      })
    );
  }

  /**
   * Sube un recurso del estudiante a una clase
   */
  subirRecursoEstudiante(data: {
    id_clase: number;
    nombre_recurso: string;
    tipo_recurso: string;
    descripcion: string;
    orden: number;
    file: File;
  }): Observable<{ ok: boolean; data?: any; msg?: string }> {
    const formData = new FormData();
    formData.append('id_clase', data.id_clase.toString());
    formData.append('nombre_recurso', data.nombre_recurso);
    formData.append('tipo_recurso', data.tipo_recurso);
    formData.append('descripcion', data.descripcion);
    formData.append('orden', data.orden.toString());
    formData.append('file', data.file);

    return this.http.post<{ ok: boolean; data?: any; msg?: string }>(
      `${this.API_URL}/recursos/estudiante`,
      formData
    ).pipe(
      tap(response => {
        if (response.ok) {
          console.log('✅ Recurso del estudiante subido:', response.data);
        }
      }),
      catchError(error => {
        console.error('❌ Error al subir recurso del estudiante:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al subir el recurso'
        });
      })
    );
  }

  /**
   * Obtiene los recursos subidos por el estudiante para una clase específica
   */
  getMisRecursosClase(idClase: number): Observable<{ ok: boolean; data?: RecursoClase[]; msg?: string }> {
    return this.http.get<{ ok: boolean; recursos?: RecursoClase[]; total?: number; msg?: string }>(
      `${this.API_URL}/recursos/mis-recursos/clase/${idClase}`
    ).pipe(
      map(response => {
        // El endpoint devuelve "recursos" en lugar de "data"
        if (response.ok && response.recursos) {
          // Construir la URL completa para recursos de estudiantes
          const recursosConURL = response.recursos.map(recurso => ({
            ...recurso,
            url_recurso: `clases/estudiantes/${recurso.url_recurso}`
          }));
          
          return {
            ok: true,
            data: recursosConURL
          };
        }
        return {
          ok: response.ok,
          data: [],
          msg: response.msg
        };
      }),
      tap(response => {
        if (response.ok && response.data) {
          console.log(`✅ Recursos del estudiante cargados para clase ${idClase}:`, response.data);
        }
      }),
      catchError(error => {
        console.error('❌ Error al obtener recursos del estudiante:', error);
        return of({
          ok: false,
          data: [],
          msg: error.error?.msg || 'Error al cargar los recursos'
        });
      })
    );
  }
}

